import { forwardRef, ChangeEvent, useState, useEffect } from "react";
import { cn } from "@/lib/utils";

interface PhoneInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
}

const formatPhoneNumber = (digits: string): string => {
  // Format digits with spaces for readability: XXX XXX XX XX pattern
  if (!digits) return '';
  
  let formatted = '';
  const parts = [
    digits.slice(0, 3),
    digits.slice(3, 6),
    digits.slice(6, 8),
    digits.slice(8, 10),
    digits.slice(10)
  ].filter(Boolean);
  
  formatted = parts.join(' ');
  return formatted;
};

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ value, onChange, className, ...props }, ref) => {
    // Extract digits from value (remove + and spaces)
    const getDigits = (val: string) => val.replace(/\D/g, '');
    
    const [digits, setDigits] = useState(getDigits(value));

    useEffect(() => {
      setDigits(getDigits(value));
    }, [value]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      // Only keep digits
      const newDigits = inputValue.replace(/\D/g, '');
      
      // Limit to 15 digits (international max)
      const limitedDigits = newDigits.slice(0, 15);
      
      setDigits(limitedDigits);
      onChange('+' + limitedDigits);
    };

    const handleFocus = () => {
      if (!digits) {
        // Pre-fill with Uzbekistan code as suggestion
        setDigits('998');
        onChange('+998');
      }
    };

    return (
      <div className={cn("flex", className)}>
        <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground text-sm">
          +
        </span>
        <input
          ref={ref}
          type="tel"
          value={formatPhoneNumber(digits)}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder="998 XX XXX XX XX"
          className={cn(
            "flex h-10 w-full rounded-md rounded-l-none border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
          )}
          {...props}
        />
      </div>
    );
  }
);

PhoneInput.displayName = "PhoneInput";

export { PhoneInput };
